import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { set } from 'lodash';
import { createContext, ReactNode, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { __LOCAL__ } from '../consts';
import useAsyncReducer from '../hooks/useAsyncReducer';
import {
	caseReducer,
	GET_MERGE_CASE,
	TCase,
	TCaseAction,
	TCaseState,
} from '../reducer';
import { IObjectContextType } from './ProviderFactory';

export type TCaseContext = IObjectContextType<TCaseState, TCaseAction> & {
	caseMutation: string;
};

// Object stored within context
export const CaseContext = createContext<TCaseContext>(undefined!);
// TODO: https://www.rivoltafilippo.com/typing-react-context-to-avoid-an-undefined-default-value

CaseContext.displayName = 'Case Context';

export const caseInitState = {
	caseData: {},
	allCases: { items: [], total: 0 },
};

const getSignedPaths = (caseData: TCase) => {
	const paths = [] as string[];

	// Return empty array if no case loaded
	if (!caseData?.id || !Object.keys(caseData).length) return paths;

	// Find all signedPaths
	const loop = (obj: { [key: string]: any } | any[], prevPath = '') => {
		for (const [key, val] of Object.entries(obj)) {
			const path = prevPath.length === 0 ? key : `${prevPath}.${key}`;

			if (key === 'signedPath' && Boolean(val)) {
				paths.push(path);
			} else if (typeof val === 'object' && val !== null) {
				loop(val, path);
			}
		}
	};

	loop(caseData);

	return paths;
};

/**
 * Global state management, using dispatches for above state calls
 * @param children Casehero project
 */
const CaseHeroProvider = ({
	children,
	caseMutation,
}: {
	children: ReactNode;
	caseMutation: string;
}) => {
	const [state, dispatch] = useAsyncReducer(caseReducer, caseInitState);
	const caseData = state.caseData;

	// Dot-notation paths of signedPath fields in state.caseData
	const [signedPathPaths, setSignedPathPaths] = useState<string[]>([]);

	// Update state every time case changes
	useEffect(() => {
		const paths = caseData?.id ? getSignedPaths(caseData) : [];
		setSignedPathPaths(paths);
	}, [caseData?.id]);

	// Get case id search param
	const [searchParams] = useSearchParams();
	const id = searchParams.get('id');

	// If search param/case id exist and match, case is open
	const isCaseOpen = id && caseData?.id ? id === caseData?.id : false;

	// Refresh signedPaths within caseData state every five minutes
	useEffect(() => {
		const refreshSignedPaths = setInterval(() => {
			// If signedPaths exist in caseData and viewing case
			if (!signedPathPaths.length && !isCaseOpen) return;

			// Generate fragment to make gql structure
			const fragment = signedPathPaths.reduce(
				(acc, path) => set(acc, path, ''),
				{}
			);

			// Create query generator input
			const query = {
				[`get${caseData.type}`]: {
					__args: {
						id: caseData.id,
					},
					...fragment,
				},
			};

			// Generate query
			const signedPathsQuery = jsonToGraphQLQuery(
				{ query },
				{
					includeFalsyKeys: true,
					pretty: true,
				}
			);

			if (__LOCAL__) {
				console.log(`Refreshing signedPaths...`);
			}
			dispatch({
				type: GET_MERGE_CASE,
				query: signedPathsQuery,
			});
		}, 15 * 60 * 1000); // 15 minutes

		// Cleanup on unmount
		return () => clearInterval(refreshSignedPaths);
	}, [signedPathPaths]);

	const value = {
		dispatch,
		state,
		mutation: caseMutation,
	} as TCaseContext;

	return (
		<CaseContext.Provider value={value}>{children}</CaseContext.Provider>
	);
};

export default CaseHeroProvider;
